import {
  STORAGE_KEYS,
  TAX_TYPES,
  APP_VERSION,
  PRINTER_COMMANDS,
  TAX_PREFERENCES,
  INVOICE_DISPLAY_OPTIONS,
  PAYMENT_MODES,
  CHARGE_CATEGORIES,
  INVOICE_HEADERS,
  SALE_INVOICE_TITLES,
  PRINTER_TYPES,
  INVOICE_TARGET_TYPE,
} from "const";
import * as TerminalHelper from "./TermialHelper";
import InvoiceFormatService from "services/InvoiceFormatService";
import * as NumberFormatHelper from "./NumberFormatHelper";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import { textToBytes, mergeBytes, padToTwoSides, wordWrap, bytesToBase64 } from "helpers/StringHelper";
import { getLogoEscPosBytes, getQrToBytes, textToEscPosBytes } from "helpers/ImageHelper";
import { generateUpiUrl } from "helpers/UpiHelper";
import { useCallback } from "react";
import { useReactToPrint } from "react-to-print";
import OnlineOrderHelper from "./OnlineOrderHelper";
import AppBridgeHelper from "helpers/AppBridgeHelper";
import { PrinterManager } from "managers/PrinterManager";
import toast from "react-hot-toast";

export function hasCashTender(sale) {
  return (sale?.payment_modes || []).some((pm) => (pm.name || "").toLowerCase() === PAYMENT_MODES.CASH.toLowerCase());
}

export default function useWebInvoicePrint({ contentRef, documentTitle, setPrintSale = null }) {
  const triggerReactToPrint = useReactToPrint({
    contentRef,
    documentTitle,
    pageStyle: `
      @page { 
        size: auto; 
        margin: 8mm; 
      }
      @media print { 
        body { 
          -webkit-print-color-adjust: exact; 
        } 
      }
    `,
  });

  const handleWebInvoicePrint = useCallback(
    async (sale) => {
      if (setPrintSale) {
        setPrintSale(sale);
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
      return triggerReactToPrint();
    },
    [triggerReactToPrint, setPrintSale],
  );

  return handleWebInvoicePrint;
}

const lineWidth = 48;

export function getInvoiceFormat() {
  const terminalId = TerminalHelper.getCurrentTerminalId();
  return getInvoiceFormatForTerminal(terminalId);
}

export function getInvoiceFormatForTerminal(terminalId) {
  try {
    const formatStore = JSON.parse(localStorage.getItem(STORAGE_KEYS.INVOICE_FORMAT) || "{}");
    return formatStore?.[terminalId];
  } catch (err) {
    console.error("Error loading invoice format", err);
  }
}

export async function loadInvoiceFormatForTerminal(terminalId) {
  try {
    const data = await InvoiceFormatService.getInvoiceFormat(terminalId);
    saveInvoiceFormatForTerminal(terminalId, data, false);
  } catch (err) {
    console.error("Error loading invoice format", err);
  }
}

export async function saveInvoiceFormatForTerminal(terminalId, data, updateServer = true) {
  try {
    if (updateServer) {
      await InvoiceFormatService.updateInvoiceFormat(terminalId, data);
    }

    const invoiceFormats = JSON.parse(localStorage.getItem(STORAGE_KEYS.INVOICE_FORMAT)) || {};
    invoiceFormats[terminalId] = data;
    localStorage.setItem(STORAGE_KEYS.INVOICE_FORMAT, JSON.stringify(invoiceFormats));
  } catch (err) {
    console.error("Error saving invoice format", err);
  }
}

export async function buildInvoiceHeader(lines = [], options = {}) {
  const invoiceFormat = getInvoiceFormat();
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);

  const printLine = (text, fontSize = 24, fontWeight = "normal") => {
    if (!text) return;
    if (fontSize > 24) {
      lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
    } else if (fontWeight === "bold") {
      lines.push(PRINTER_COMMANDS.CHARS_SIZE_BOLD);
    }
    lines.push(textToBytes(`${text}\n`));
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  };

  if (invoiceFormat.shop_name) {
    printLine(invoiceFormat.shop_name, 32, "bold");
  }
  if (invoiceFormat.address_1) {
    printLine(invoiceFormat.address_1, 22);
  }
  if (invoiceFormat.address_2) {
    printLine(invoiceFormat.address_2, 22);
  }
  if (invoiceFormat.address_3) {
    printLine(invoiceFormat.address_3, 22);
  }
  if (invoiceFormat.address_4) {
    printLine(invoiceFormat.address_4, 22);
  }
  if (invoiceFormat.tel_no) {
    printLine(invoiceFormat.tel_no);
  }
  if (invoiceFormat.tin) {
    printLine(invoiceFormat.tin);
  }
  if (!options.skipCustomHeader) {
    const headerText = options.sale?.bill_no
      ? invoiceFormat.header || INVOICE_HEADERS.TAX_INVOICE
      : INVOICE_HEADERS.ESTIMATE;
    printLine(headerText, 24, "bold");
  } else {
    lines.push(textToBytes("\n"));
  }
}

export function getDisplayRate(saleItem, displayOption) {
  const rate = Number(saleItem.rate || 0);
  const tax = Number(saleItem.tax || 0);
  const cess = Number(saleItem.cess || 0);
  const qty = Number(saleItem.qty || 1) || 1;
  const baserate = Number(saleItem.item?.price || 0);

  switch (displayOption) {
    case INVOICE_DISPLAY_OPTIONS.ACTUAL_RATE.value:
      return rate;

    case INVOICE_DISPLAY_OPTIONS.ACTUAL_RATE_PLUS_TAX.value:
      return rate + tax / qty + cess / qty;

    case INVOICE_DISPLAY_OPTIONS.BASE_RATE.value:
      return baserate;

    case INVOICE_DISPLAY_OPTIONS.BASE_RATE_PLUS_TAX.value:
      return baserate + tax / qty + cess / qty;

    case INVOICE_DISPLAY_OPTIONS.PRICE.value:
      return Number(saleItem.item?.price || saleItem.rate || 0);

    default:
      return rate;
  }
}

function appendInvoiceTitles(lines, titles) {
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_BOLD);
  for (const title of titles) {
    lines.push(textToBytes(`[${title}]\n`));
  }
  lines.push(textToBytes("\n"));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
}

function appendInvoiceMeta(lines, sale, formattedDate, formattedTime) {
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
  lines.push(textToBytes(padToTwoSides(`Inv No: ${sale.bill_no || "-"}`, `Time: ${formattedTime}`)));
  lines.push(textToBytes(padToTwoSides(`Cart No: ${sale?.local_id ?? "-"}`, `Date: ${formattedDate}`)));

  if (sale?.table) {
    const chairSuffix = sale?.table?.chair?.name ? ` > ${sale.table.chair.name}` : "";
    const tableName = `${sale?.table?.area?.name} > ${sale?.table?.name}${chairSuffix}`;
    lines.push(textToBytes(padToTwoSides(`${tableName}`, `${sale?.salesperson?.name || ""}`)));
  }
  if (sale?.order_type) {
    lines.push(textToBytes(padToTwoSides(`Order Type: ${sale?.order_type}`, "")));
  }
  if (sale?.waiter) {
    lines.push(textToBytes(padToTwoSides(`Sale Staff: ${sale?.waiter?.name}`, "")));
  }
}

function appendInvoiceCustomerDetails(lines, customer) {
  if (!customer) return;
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(textToBytes("CUSTOMER DETAILS\n"));
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
  if (customer.name || customer.mobile) {
    lines.push(textToBytes(padToTwoSides(customer.name || "", customer.mobile || "")));
  }
  if (customer.location || customer.trn_number) {
    lines.push(textToBytes(padToTwoSides(customer.location || "", customer.trn_number || "")));
  }
}

function renderItemChargesIfPresent(lines, item, showItemCharges) {
  if (showItemCharges && item.charges && item.charges.length > 0) {
    item.charges.forEach((charge) => {
      const chargeName = `${charge.name || "Charge"}${charge.category === CHARGE_CATEGORIES.DISCOUNT ? " (-)" : ""}`;
      const chargeVal = charge.value || 0;
      const prefixSpaces = " ".repeat(6);
      const namePart = `${prefixSpaces}${chargeName}`.padEnd(lineWidth - 8).slice(0, lineWidth - 8);
      const valuePart = NumberFormatHelper.roundOff(chargeVal).toString().padStart(8);
      lines.push(textToBytes(`${namePart}${valuePart}\n`));
    });
  }
}

function renderSingleInvoiceItem(lines, item, index, showMRP, showHSN, showItemCharges, invoiceDisplayRate) {
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
  const rate = getDisplayRate(item, invoiceDisplayRate);
  const totalAmount = rate * item.qty;
  const name = `${item.name || "-"}${item.option?.label ? ` - ${item.option.label}` : ""}`;
  const showItemHSN = showHSN && item.hsn && item.hsn !== "0" && item.hsn !== 0;
  const finalName = showItemHSN ? `${name}  - HSN: ${item.hsn}` : name;

  const serialPrefix =
    String(index + 1)
      .padEnd(2)
      .slice(0, 2) + " ";
  const indexWidth = 3;
  const combinedName = `${serialPrefix}${finalName}`;

  const qty = item.qty;
  const mrp = Number(item.item?.mrp || 0);
  const basePrice = Number(item.item?.price ?? rate);
  let mrpSavings = 0;
  if (mrp > basePrice) {
    mrpSavings = (mrp - basePrice) * qty;
  }

  const numericWidth = showMRP ? 29 : 21;
  const availableNameWidth = lineWidth - numericWidth;

  const qtyPart = NumberFormatHelper.quantityRoundOffString(qty).padStart(5);
  const mrpPart = showMRP ? NumberFormatHelper.roundOff(mrp).padStart(8) : "";
  const ratePart = NumberFormatHelper.roundOff(rate).padStart(8);
  const amtPart = NumberFormatHelper.roundOff(totalAmount).padStart(8);
  const numericPart = `${qtyPart}${mrpPart}${ratePart}${amtPart}`;

  if (combinedName.length <= availableNameWidth) {
    lines.push(textToBytes(`${combinedName.padEnd(availableNameWidth)}${numericPart}\n`));
  } else {
    const nameLines = wordWrap(finalName, lineWidth - indexWidth);
    nameLines.forEach((line, i) => {
      const prefix = i === 0 ? serialPrefix : " ".repeat(indexWidth);
      lines.push(textToBytes(`${prefix}${line}\n`));
    });
    lines.push(textToBytes(" ".repeat(lineWidth - numericWidth) + numericPart + "\n"));
  }

  renderItemChargesIfPresent(lines, item, showItemCharges);

  const itemTotal = showItemCharges
    ? Number(NumberFormatHelper.roundOff(item.totalBeforeTax))
    : Number(NumberFormatHelper.roundOff(totalAmount));
  return { qty, mrpSavings, itemTotal };
}

function appendInvoiceItems(lines, items, showMRP, showHSN, showItemCharges, terminalSettings) {
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  lines.push(textToBytes("=".repeat(lineWidth)));

  const header = showMRP
    ? `# `.padEnd(3) +
      `Item`.padEnd(16) +
      `Qty`.padStart(5) +
      `MRP`.padStart(8) +
      `Rate`.padStart(8) +
      `Amount`.padStart(8)
    : `# `.padEnd(3) + `Item`.padEnd(24) + `Qty`.padStart(5) + `Rate`.padStart(8) + `Amount`.padStart(8);

  lines.push(textToBytes(header));
  lines.push(textToBytes("-".repeat(lineWidth)));

  let quantitySum = 0;
  let totalMrpSavings = 0;
  let displayTotal = 0;

  if (items.length === 0) {
    lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
    lines.push(textToBytes("No items"));
  } else {
    items.forEach((item, index) => {
      const res = renderSingleInvoiceItem(
        lines,
        item,
        index,
        showMRP,
        showHSN,
        showItemCharges,
        terminalSettings.invoiceDisplayRate,
      );
      quantitySum += res.qty;
      totalMrpSavings += res.mrpSavings;
      displayTotal += res.itemTotal;
    });
  }

  lines.push(textToBytes("-".repeat(lineWidth)));
  const numericWidthTotal = showMRP ? 29 : 21;
  const namePartWidth = lineWidth - numericWidthTotal;
  const namePart = `${items.length} items`.padEnd(namePartWidth).slice(0, namePartWidth);
  const qtyPart = NumberFormatHelper.quantityRoundOffString(quantitySum).padStart(5);
  const amountSumStr = NumberFormatHelper.roundOff(displayTotal).padStart(lineWidth - namePartWidth - 5);
  lines.push(textToBytes(`${namePart}${qtyPart}${amountSumStr}`));

  return { quantitySum, totalMrpSavings, displayTotal };
}

function appendInvoiceChargesAndDiscounts(lines, sale, showItemCharges, summaryKeyWidth, summaryValueWidth) {
  const saleCharges = (sale?.charges || []).filter((c) => Number(c?.value) !== 0);
  const extraDiscount = Number(sale?.extraDiscount || 0);
  const itemCharges = !showItemCharges
    ? Math.max(0, Number(sale?.totalChargeAmount || 0) - Number(sale?.totalSaleChargeAmount || 0))
    : 0;
  const itemDiscount = !showItemCharges
    ? Number(sale?.discount_applied_on_items || 0) + Number(sale?.discount || 0)
    : 0;

  if (saleCharges.length === 0 && extraDiscount === 0 && itemCharges === 0 && itemDiscount === 0) {
    return;
  }

  lines.push(textToBytes("-".repeat(lineWidth)));

  saleCharges.forEach((charge) => {
    const isDiscount = charge.category === CHARGE_CATEGORIES.DISCOUNT;
    const chargeLabel = `${charge.name}${isDiscount ? " (-)" : ""}: `;
    const formattedVal = NumberFormatHelper.roundOff(charge.value || 0);
    if (chargeLabel.length <= summaryKeyWidth) {
      lines.push(textToBytes(chargeLabel.padStart(summaryKeyWidth) + formattedVal.padStart(summaryValueWidth)));
    } else {
      lines.push(textToBytes(padToTwoSides(chargeLabel.trim(), formattedVal, lineWidth)));
    }
  });

  if (extraDiscount > 0) {
    const label = "Extra Discount (-): ";
    const formattedVal = NumberFormatHelper.roundOff(extraDiscount);
    lines.push(textToBytes(label.padStart(summaryKeyWidth) + formattedVal.padStart(summaryValueWidth)));
  }

  if (itemDiscount > 0) {
    const formattedVal = NumberFormatHelper.roundOff(itemDiscount);
    lines.push(textToBytes("Total Discount: ".padStart(summaryKeyWidth) + formattedVal.padStart(summaryValueWidth)));
  }

  if (itemCharges > 0) {
    const formattedVal = NumberFormatHelper.roundOff(itemCharges);
    lines.push(textToBytes("Item Charges: ".padStart(summaryKeyWidth) + formattedVal.padStart(summaryValueWidth)));
  }
}

function appendTaxBreakdownLines(lines, sale, shop, taxPreference, summaryKeyWidth, summaryValueWidth) {
  if (taxPreference === TAX_PREFERENCES.NO_TAX) return;

  lines.push(textToBytes("-".repeat(lineWidth)));
  const totalBeforeTaxString = NumberFormatHelper.roundOff(sale?.payable - (sale?.tax + sale?.cess)).padStart(
    summaryValueWidth,
  );
  lines.push(textToBytes("Total before tax: ".padStart(summaryKeyWidth) + totalBeforeTaxString));

  if (sale?.tax && sale?.tax !== 0) {
    if (shop?.tax_type === TAX_TYPES.GST) {
      const cgst = sale?.tax / 2;
      lines.push(
        textToBytes("CGST: ".padStart(summaryKeyWidth) + NumberFormatHelper.roundOff(cgst).padStart(summaryValueWidth)),
      );
      lines.push(
        textToBytes("SGST: ".padStart(summaryKeyWidth) + NumberFormatHelper.roundOff(cgst).padStart(summaryValueWidth)),
      );
    } else if (shop?.tax_type === TAX_TYPES.IGST) {
      lines.push(
        textToBytes(
          "IGST: ".padStart(summaryKeyWidth) + NumberFormatHelper.roundOff(sale?.tax).padStart(summaryValueWidth),
        ),
      );
    } else if (shop?.tax_type === TAX_TYPES.VAT) {
      lines.push(
        textToBytes(
          "VAT: ".padStart(summaryKeyWidth) + NumberFormatHelper.roundOff(sale?.tax).padStart(summaryValueWidth),
        ),
      );
    } else {
      lines.push(
        textToBytes(
          "Tax Amount: ".padStart(summaryKeyWidth) + NumberFormatHelper.roundOff(sale?.tax).padStart(summaryValueWidth),
        ),
      );
    }
  }

  if (sale?.cess && sale?.cess !== 0) {
    lines.push(
      textToBytes(
        "CESS: ".padStart(summaryKeyWidth) + NumberFormatHelper.roundOff(sale?.cess).padStart(summaryValueWidth),
      ),
    );
  }
}

function appendInvoiceTaxAndTotals(lines, sale, shop, taxPreference, itemsRes, showItemCharges, printTotalSave) {
  const summaryValueWidth = 14;
  const summaryKeyWidth = lineWidth - summaryValueWidth;

  appendInvoiceChargesAndDiscounts(lines, sale, showItemCharges, summaryKeyWidth, summaryValueWidth);
  appendTaxBreakdownLines(lines, sale, shop, taxPreference, summaryKeyWidth, summaryValueWidth);

  const transactionDiscount =
    Number(sale?.discount_applied_on_items ?? 0) + Number(sale?.extraDiscount ?? 0) + Number(sale?.discount ?? 0);
  const grandTotalSavings = itemsRes.totalMrpSavings + transactionDiscount;

  if (printTotalSave && grandTotalSavings > 0 && transactionDiscount > 0) {
    lines.push(
      textToBytes(
        "YOU SAVED TOTAL: ".padStart(summaryKeyWidth) +
          NumberFormatHelper.roundOff(grandTotalSavings).padStart(summaryValueWidth),
      ),
    );
  }

  lines.push(textToBytes("=".repeat(lineWidth)));

  const roundOff = Number(sale?.roundOff ?? 0);
  if (roundOff !== 0) {
    lines.push(textToBytes(padToTwoSides("ROUND OFF", NumberFormatHelper.roundOff(roundOff))));
  }

  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes(padToTwoSides("SUB TOTAL", NumberFormatHelper.roundOff(sale?.payable), lineWidth / 2)));

  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);

  if (sale?.prepaid && Number(sale?.prepaid) !== Number(sale?.payable)) {
    lines.push(textToBytes(padToTwoSides("PREPAID: ", NumberFormatHelper.roundOff(sale.prepaid))));
  }

  if (sale?.due && Number(sale?.due) !== 0) {
    lines.push(textToBytes(padToTwoSides("DUE: ", NumberFormatHelper.roundOff(sale.due))));
  }

  lines.push(textToBytes(wordWrap(`In words: ${NumberFormatHelper.toWords(sale?.payable ?? 0)}\n`, lineWidth)));

  if (taxPreference !== TAX_PREFERENCES.NO_TAX) {
    lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
    lines.push(textToBytes("INCLUSIVE OF ALL TAXES\n"));
    lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
    lines.push(textToBytes("=".repeat(lineWidth)));
  }
}

async function appendPaymentQrCodeIfEnabled(lines, sale, shop, terminalSettings) {
  const hasUPIPayment = (sale?.payment_modes || []).some((pm) => pm.name?.toLowerCase() === PAYMENT_MODES.UPI);
  const showPaymentQr = !!terminalSettings?.upi_id && (!!terminalSettings?.printPaymentQrOnInvoice || hasUPIPayment);

  if (showPaymentQr) {
    const upiUrl = generateUpiUrl({
      upiId: terminalSettings.upi_id,
      name: shop?.shop_name,
      amount: NumberFormatHelper.roundOff(sale?.payable),
    });
    const qrBytes = await getQrToBytes(upiUrl, 200);
    if (qrBytes) {
      lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
      lines.push(textToBytes("\nScan QR Code for Payment\n"));
      lines.push(qrBytes);
      lines.push(textToBytes("\n"));
    }
  }
}

function appendCounterFoilSectionIfEnabled(lines, sale, formattedDate, formattedTime, isCounterFoilEnabled) {
  if (isCounterFoilEnabled) {
    lines.push(PRINTER_COMMANDS.CUT_CHARS);
    lines.push(PRINTER_COMMANDS.BEEP_SOUND);
    lines.push(textToBytes("\n"));
    lines.push(textToBytes(padToTwoSides(`Date: ${formattedDate}`, `Time: ${formattedTime}`), lineWidth / 2));
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_BOLD);
    lines.push(textToBytes("\n"));
    lines.push(textToBytes(`Inv No: ${sale.bill_no || "-"}\n`));
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
    lines.push(textToBytes("\n"));
    lines.push(textToBytes(`Sub Total: ${NumberFormatHelper.roundOff(sale.payable) || "-"}\n`, lineWidth / 2));
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
    lines.push(PRINTER_COMMANDS.FEED_PAPER);
  }
}

function appendReceiptPaymentSummary(lines, sale, paymentModeOnInvoice) {
  if (!paymentModeOnInvoice) return;

  const receivedModes = (sale.payment_modes || []).filter((pm) => pm?.id && Number(pm.amount || 0) > 0);
  const changeModes = (sale.payment_modes || []).filter((pm) => pm?.id && Number(pm.amount || 0) < 0);
  const totalTendered = receivedModes.reduce((sum, pm) => sum + Number(pm.amount || 0), 0);

  receivedModes.forEach((pm) => {
    lines.push(textToBytes(padToTwoSides(`${pm.name || "-"}:`, NumberFormatHelper.roundOff(pm.amount || 0))));
  });

  changeModes.forEach((pm) => {
    lines.push(textToBytes(padToTwoSides(`${pm.name || "-"}:`, NumberFormatHelper.roundOff(pm.amount || 0))));
  });
  lines.push(textToBytes(padToTwoSides("TOTAL TENDER:", NumberFormatHelper.roundOff(totalTendered))));
}

function appendReceiptCustomerCredits(lines, sale) {
  if (sale?.loyalty_points_amount) {
    lines.push(textToBytes(padToTwoSides("LOYALTY POINT:", NumberFormatHelper.roundOff(sale.loyalty_points_amount))));
  }

  if (!sale?.customer_credit_used) return;

  lines.push(textToBytes(padToTwoSides("CUSTOMER CREDIT:", NumberFormatHelper.roundOff(sale.customer_credit_used))));

  lines.push(
    textToBytes(
      padToTwoSides(
        "PREV. BALANCE:",
        NumberFormatHelper.customerAmountFormat((sale.customer?.balance || 0) + sale.customer_credit_used),
      ),
    ),
  );
}

function appendReceiptCustomerInfo(lines, sale) {
  if (sale?.customer) {
    lines.push(
      textToBytes(padToTwoSides("CUSTOMER BALANCE:", NumberFormatHelper.customerAmountFormat(sale.customer.balance))),
    );
  }

  if (sale.change && Number(sale.change) !== 0) {
    lines.push(textToBytes(padToTwoSides("CHANGE:", NumberFormatHelper.roundOff(sale.change))));
  }
}

function appendReceiptToken(lines, sale) {
  if (!sale?.token_no) return;

  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(textToBytes(`\nTOKEN NO: ${sale.token_no}\n`));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
}
export function parseInvoiceNotes(note) {
  if (!note) return [];
  const notes = Array.isArray(note) ? note : String(note).split(/,|\r?\n/);
  return notes.map((item) => (typeof item === "string" ? item.trim() : item)).filter(Boolean);
}

function appendReceiptFooter(lines, invoiceFormat) {
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);

  if (invoiceFormat.footer_1) {
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_BOLD);
    lines.push(textToBytes(`${invoiceFormat.footer_1}\n`));
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  }

  if (invoiceFormat.footer_2) {
    lines.push(textToBytes(`${invoiceFormat.footer_2}\n`));
  }
  if (invoiceFormat?.note) {
    lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
    lines.push(textToBytes(`Notes:-\n`));
    for (const note of parseInvoiceNotes(invoiceFormat.note)) {
      const wrapped = wordWrap(note, lineWidth).join("\n");
      lines.push(textToBytes(`${wrapped}\n`));
    }
    lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  }

  lines.push(textToBytes(`v${APP_VERSION}`));
  lines.push(PRINTER_COMMANDS.FEED_PAPER);
}
async function appendInvoicePaymentAndFooter(
  lines,
  sale,
  shop,
  terminalSettings,
  paymentModeOnInvoice,
  formattedDate,
  formattedTime,
  isCounterFoilEnabled,
) {
  appendReceiptPaymentSummary(lines, sale, paymentModeOnInvoice);
  appendReceiptCustomerCredits(lines, sale);
  appendReceiptCustomerInfo(lines, sale);
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  appendReceiptToken(lines, sale);
  await appendPaymentQrCodeIfEnabled(lines, sale, shop, terminalSettings);
  appendReceiptFooter(lines, getInvoiceFormat());
  appendCounterFoilSectionIfEnabled(lines, sale, formattedDate, formattedTime, isCounterFoilEnabled);
}

async function appendReceiptLogoIfPresent(lines, logoUrl) {
  if (!logoUrl) return;
  const logoBytes = await getLogoEscPosBytes(logoUrl);
  if (logoBytes != null) {
    lines.push(logoBytes);
    lines.push(textToBytes("\n"));
  }
}

export async function getThreeInchInvoice(sale = {}, shop = {}, titles = []) {
  let lines = [];
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  await appendReceiptLogoIfPresent(lines, shop?.shop_logo);

  const terminalSettings = TerminalHelper.getTerminalSettings() || {};
  const {
    printMrpOnInvoice: showMRP,
    printHsnOnInvoice: showHSN,
    paymentModeOnInvoice,
    counterFoil: isCounterFoilEnabled,
    showItemChargesOnInvoice: showItemCharges,
    printTotalSave,
  } = terminalSettings;
  const taxPreference = localStorage.getItem(STORAGE_KEYS.TAX_PREFERENCE);

  const formattedDate = DateTimeHelpers.getDate(sale.time);
  const formattedTime = DateTimeHelpers.getLocalTime(sale.time);

  await buildInvoiceHeader(lines, { sale });
  appendInvoiceTitles(lines, titles);
  appendInvoiceMeta(lines, sale, formattedDate, formattedTime);
  appendInvoiceCustomerDetails(lines, sale?.customer);

  const itemsRes = appendInvoiceItems(lines, sale.items || [], showMRP, showHSN, showItemCharges, terminalSettings);
  appendInvoiceTaxAndTotals(lines, sale, shop, taxPreference, itemsRes, showMRP, showItemCharges, printTotalSave);
  await appendInvoicePaymentAndFooter(
    lines,
    sale,
    shop,
    terminalSettings,
    paymentModeOnInvoice,
    formattedDate,
    formattedTime,
    isCounterFoilEnabled,
  );

  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);

  const duplicate = (titles || []).some((title) => (title || "") === SALE_INVOICE_TITLES.DUPLICATE);

  if (!duplicate && hasCashTender(sale)) {
    lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  }

  return mergeBytes(lines);
}

async function appendMultiLangInvoiceMeta(addLine, format, titles, sale, isArabic, formattedDateTime) {
  if (format?.shop_name) await addLine(format?.shop_name, { size: "double", weight: "bold", align: "center" });
  if (format?.address_1) await addLine(format?.address_1, { align: "center" });
  if (format?.address_2) await addLine(format?.address_2, { align: "center" });
  if (format?.address_3) await addLine(format?.address_3, { align: "center" });
  if (format?.address_4) await addLine(format?.address_4, { align: "center" });
  if (format?.tel_no) await addLine(`Phone: +${format?.tel_no}`, { align: "center" });
  if (format?.tin) await addLine(format?.tin, { align: "center" });
  if (format?.header) await addLine(format?.header, { weight: "bold", align: "center" });

  for (const title of titles) {
    await addLine(title, { weight: "bold", align: "center", lineHeight: 40 });
  }

  const billNoLabel = isArabic ? "Inv No / رقم الفاتورة\u200e" : "Inv No";
  const dateLabel = isArabic ? "Date / تاريخ\u200e" : "Date";

  await addLine([
    { text: `${billNoLabel}:`, width: "50%", align: "left" },
    { text: ` ${sale.bill_no || "-"}`, width: "50%", align: "right" },
  ]);

  await addLine([
    { text: `${dateLabel}:`, width: "50%", align: "left" },
    { text: `${formattedDateTime}`, width: "50%", align: "right" },
  ]);

  if (sale?.table) {
    const chairSuffix = sale?.table?.chair?.name ? ` > ${sale.table.chair.name}` : "";
    const tableName = `${sale?.table?.area?.name} > ${sale?.table?.name}${chairSuffix}`;
    await addLine([
      { text: tableName, width: "60%", align: "left" },
      { text: sale?.salesperson?.name || "", width: "40%", align: "right" },
    ]);
  }
  if (sale?.order_type) {
    await addLine(`Order Type: ${sale?.order_type}`);
  }

  if (sale?.waiter) {
    await addLine(`Sales Staff: ${sale?.waiter?.name}`);
  }

  await appendMultiLangInvoiceCustomerDetails(addLine, sale?.customer, isArabic);
}

async function appendMultiLangInvoiceCustomerDetails(addLine, customer, isArabic) {
  if (!customer) return;
  const customerLabel = isArabic ? "CUSTOMER DETAILS / تفاصيل العميل" : "CUSTOMER DETAILS";
  await addLine(customerLabel, { align: "center" });
  if (customer.name || customer.mobile) {
    await addLine([
      { text: customer.name, width: "50%", align: "left" },
      { text: customer.mobile, width: "50%", align: "right" },
    ]);
  }
  if (customer.location || customer.trn_number) {
    await addLine([
      { text: customer.location, width: "50%", align: "left" },
      { text: customer.trn_number, width: "50%", align: "right" },
    ]);
  }
}

async function appendMultiLangInvoiceItemRow(addLine, item, index, showMRP, showHSN, invoiceDisplayRate) {
  const rate = getDisplayRate(item, invoiceDisplayRate);
  const totalBeforeTax = rate * item.qty;
  const name = `${item.name || "-"}${item.option?.label ? ` - ${item.option.label}` : ""}`;
  const showItemHSN = showHSN && item.hsn && item.hsn !== "0" && item.hsn !== 0;
  const finalItemName = showItemHSN ? `${name}  - HSN: ${item.hsn}` : name;

  const mrp = Number(item.item?.mrp || 0);
  const basePrice = Number(item.item?.price ?? rate);
  const mrpSavings = mrp > basePrice ? (mrp - basePrice) * item.qty : 0;
  const qtyP = NumberFormatHelper.quantityRoundOffString(item.qty);
  const mrpP = showMRP ? NumberFormatHelper.roundOff(item.item.mrp) : "";
  const rateP = NumberFormatHelper.roundOff(rate);
  const amtP = NumberFormatHelper.roundOff(totalBeforeTax);

  if (showMRP) {
    await addLine([
      { text: String(index + 1), width: "8%", align: "left" },
      { text: finalItemName, width: "30%", align: "left" },
      { text: qtyP, width: "10%", align: "right" },
      { text: mrpP, width: "17%", align: "right" },
      { text: rateP, width: "17%", align: "right" },
      { text: amtP, width: "18%", align: "right" },
    ]);
  } else {
    await addLine([
      { text: String(index + 1), width: "8%", align: "left" },
      { text: finalItemName, width: "48%", align: "left" },
      { text: qtyP, width: "10%", align: "right" },
      { text: rateP, width: "17%", align: "right" },
      { text: amtP, width: "17%", align: "right" },
    ]);
  }

  return { qty: item.qty, mrpSavings, totalBeforeTax };
}

function getInvoiceHeaderLabels(isArabic) {
  return {
    itemLabel: isArabic ? "Item\nالصنف" : "Item",
    qtyLabel: isArabic ? "Qty\nالكمية" : "Qty",
    rateLabel: isArabic ? "Rate\nسعر" : "Rate",
    amountLabel: isArabic ? "Amount\nمبلغ" : "Amount",
  };
}

async function appendInvoiceItemsSummary(addLine, itemsCount, quantitySum, displayTotal, showMRP) {
  if (showMRP) {
    await addLine([
      { text: `${itemsCount} items`, width: "38%", align: "left" },
      {
        text: NumberFormatHelper.quantityRoundOffString(quantitySum),
        width: "10%",
        align: "right",
      },
      { text: "", width: "17%", align: "right" },
      { text: "", width: "17%", align: "right" },
      {
        text: NumberFormatHelper.roundOff(displayTotal),
        width: "18%",
        align: "right",
      },
    ]);
    return;
  }

  await addLine([
    { text: `${itemsCount} items`, width: "50%", align: "left" },
    {
      text: NumberFormatHelper.quantityRoundOffString(quantitySum),
      width: "15%",
      align: "right",
    },
    { text: "", width: "15%", align: "right" },
    {
      text: NumberFormatHelper.roundOff(displayTotal),
      width: "20%",
      align: "right",
    },
  ]);
}

async function appendMultiLangInvoiceItems(addLine, itemsList, showMRP, showHSN, invoiceDisplayRate, isArabic) {
  await addLine("=".repeat(lineWidth));
  const { itemLabel, qtyLabel, rateLabel, amountLabel } = getInvoiceHeaderLabels(isArabic);
  if (showMRP) {
    const mrpLabel = "MRP";
    await addLine([
      { text: "#", width: "8%", align: "left" },
      { text: itemLabel, width: "30%", align: "left" },
      { text: qtyLabel, width: "10%", align: "right" },
      { text: mrpLabel, width: "17%", align: "right" },
      { text: rateLabel, width: "17%", align: "right" },
      { text: amountLabel, width: "18%", align: "right" },
    ]);
  } else {
    await addLine([
      { text: "#", width: "8%", align: "left" },
      { text: itemLabel, width: "48%", align: "left" },
      { text: qtyLabel, width: "10%", align: "right" },
      { text: rateLabel, width: "17%", align: "right" },
      { text: amountLabel, width: "17%", align: "right" },
    ]);
  }
  await addLine("-".repeat(lineWidth));
  let quantitySum = 0;
  let totalMrpSavings = 0;
  let displayTotal = 0;
  for (let index = 0; index < itemsList.length; index++) {
    const item = itemsList[index];
    const res = await appendMultiLangInvoiceItemRow(addLine, item, index, showMRP, showHSN, invoiceDisplayRate);
    quantitySum += res.qty;
    totalMrpSavings += res.mrpSavings;
    displayTotal += res.totalBeforeTax;
  }
  await addLine("-".repeat(lineWidth));
  await appendInvoiceItemsSummary(addLine, itemsList.length, quantitySum, displayTotal, showMRP);
  return { quantitySum, totalMrpSavings, displayTotal };
}

async function appendMultiLangTaxBreakdown(addLine, sale, shop, taxPreference, isArabic, summaryValueWidth) {
  if (taxPreference === TAX_PREFERENCES.NO_TAX) return;

  const totalBeforeTaxLabel = isArabic ? "Total before tax /الإجمالي قبل الضريبة:" : "Total before tax:";
  await addLine("-".repeat(lineWidth));
  const totalBeforeTaxString = NumberFormatHelper.roundOff(sale?.payable - (sale?.tax + sale?.cess)).padStart(
    summaryValueWidth,
  );

  await addLine([
    { text: totalBeforeTaxLabel, width: "75%", align: "left" },
    { text: totalBeforeTaxString, width: "25%", align: "right" },
  ]);

  if (sale?.tax && sale?.tax !== 0) {
    if (shop?.tax_type === TAX_TYPES.GST) {
      const cgst = sale.tax / 2;
      await addLine([
        { text: "CGST: ", width: "50%", align: "left" },
        { text: NumberFormatHelper.roundOff(cgst), width: "50%", align: "right" },
      ]);
      await addLine([
        { text: "SGST: ", width: "50%", align: "left" },
        { text: NumberFormatHelper.roundOff(cgst), width: "50%", align: "right" },
      ]);
    } else if (shop?.tax_type === TAX_TYPES.VAT) {
      const vatLabel = isArabic ? "TAX/ضريبة" : "TAX";
      await addLine([
        { text: vatLabel, width: "50%", align: "left" },
        { text: NumberFormatHelper.roundOff(sale?.tax), width: "50%", align: "right" },
      ]);
    } else {
      const taxLabel = isArabic ? "Tax Amount\nمبلغ الضريبة" : "Tax Amount";
      await addLine([
        { text: taxLabel, width: "50%", align: "left" },
        { text: NumberFormatHelper.roundOff(sale?.tax), width: "50%", align: "right" },
      ]);
    }
  }

  if (sale?.cess && sale?.cess !== 0) {
    await addLine([
      { text: "CESS: ", width: "50%", align: "left" },
      { text: NumberFormatHelper.roundOff(sale?.cess), width: "50%", align: "right" },
    ]);
  }
}

async function appendMultiLangInvoiceChargesAndDiscounts(addLine, sale, isArabic) {
  const terminalSettings = TerminalHelper.getTerminalSettings();
  const showItemCharges = !!terminalSettings?.showItemChargesOnInvoice;

  const saleCharges = (sale?.charges || []).filter((c) => Number(c?.value) !== 0);
  const extraDiscount = Number(sale?.extraDiscount || 0);
  const itemCharges = !showItemCharges
    ? Math.max(0, Number(sale?.totalChargeAmount || 0) - Number(sale?.totalSaleChargeAmount || 0))
    : 0;
  const totalDiscount = Number(sale?.discount_applied_on_items || 0) + Number(sale?.discount || 0);
  if (saleCharges.length === 0 && extraDiscount === 0 && totalDiscount === 0 && itemCharges === 0) {
    return;
  }

  await addLine("-".repeat(lineWidth));
  for (const charge of saleCharges) {
    const chargeVal = charge.value || 0;
    const isDiscount = charge.category === CHARGE_CATEGORIES.DISCOUNT;
    const chargeLabel = `${charge.name}${isDiscount ? " (-)" : ""}:`;
    await addLine([
      { text: chargeLabel, width: "60%", align: "left" },
      { text: NumberFormatHelper.roundOff(chargeVal), width: "40%", align: "right" },
    ]);
  }

  if (extraDiscount > 0) {
    const extraDiscountLabel = isArabic ? "Extra Discount /خصم إضافي (-):" : "Extra Discount (-):";
    await addLine([
      { text: extraDiscountLabel, width: "60%", align: "left" },
      { text: NumberFormatHelper.roundOff(extraDiscount), width: "40%", align: "right" },
    ]);
  }

  if (totalDiscount > 0) {
    const totalDiscountLabel = isArabic ? "Total Discount /إجمالي الخصم:" : "Total Discount:";

    await addLine([
      { text: totalDiscountLabel, width: "60%", align: "left" },
      { text: NumberFormatHelper.roundOff(totalDiscount), width: "40%", align: "right" },
    ]);
  }
  if (itemCharges > 0) {
    const itemChargesLabel = isArabic ? "Item Charges /رسوم الأصناف:" : "Item Charges:";
    await addLine([
      { text: itemChargesLabel, width: "60%", align: "left" },
      { text: NumberFormatHelper.roundOff(itemCharges), width: "40%", align: "right" },
    ]);
  }
}

async function appendInvoiceTotalSavings(addLine, sale, itemsRes, isArabic, printTotalSave) {
  if (!printTotalSave) return;

  const transactionDiscount =
    Number(sale?.discount_applied_on_items ?? 0) + Number(sale?.extraDiscount ?? 0) + Number(sale?.discount ?? 0);
  const grandTotalSavings = itemsRes.totalMrpSavings + transactionDiscount;

  if (grandTotalSavings <= 0) return;

  const youSavedTotalLabel = isArabic ? "YOU SAVED TOTAL / لقد وفرت الإجمالي:" : "YOU SAVED TOTAL:";

  await addLine([
    { text: youSavedTotalLabel, width: "70%", align: "left" },
    {
      text: NumberFormatHelper.roundOff(grandTotalSavings),
      width: "30%",
      align: "right",
    },
  ]);
}

async function appendInvoicePaymentModes(addLine, sale, paymentModeOnInvoice) {
  if (!paymentModeOnInvoice) return;

  const paymentModes = sale.payment_modes || [];

  for (const paymentMode of paymentModes) {
    if (!paymentMode?.id) continue;

    await addLine([
      {
        text: `${paymentMode.name || "-"}:`,
        width: "70%",
        align: "left",
      },
      {
        text: NumberFormatHelper.roundOff(paymentMode.amount || 0),
        width: "30%",
        align: "right",
      },
    ]);
  }
  if (!paymentModeOnInvoice) return;

  const totalTendered = paymentModes.reduce((sum, pm) => sum + Number(pm.amount || 0), 0);

  await addLine([
    { text: "TOTAL TENDER:", width: "50%", align: "left" },
    { text: NumberFormatHelper.roundOff(totalTendered), width: "50%", align: "right" },
  ]);
}

async function appendInvoiceCustomerBalance(addLine, sale, isArabic) {
  if (sale?.loyalty_points_amount) {
    const loyaltyPointLabel = isArabic ? "LOYALTY POINT / نقاط الولاء: " : "LOYALTY POINT:";
    await addLine([
      { text: loyaltyPointLabel, width: "70%", align: "left" },
      {
        text: NumberFormatHelper.roundOff(sale.loyalty_points_amount),
        width: "30%",
        align: "right",
      },
    ]);
  }

  if (sale?.customer_credit_used) {
    const creditUsedLabel = isArabic ? "CUSTOMER CREDIT / ائتمان العميل:" : "CUSTOMER CREDIT:";
    await addLine([
      { text: creditUsedLabel, width: "70%", align: "left" },
      {
        text: NumberFormatHelper.roundOff(sale.customer_credit_used),
        width: "30%",
        align: "right",
      },
    ]);
  }

  if (!sale?.customer) return;
  const customerPreviousBalanceLabel = isArabic ? "Prev. BALANCE / الرصيد السابق:" : "Prev. BALANCE:";

  await addLine([
    { text: customerPreviousBalanceLabel, width: "70%", align: "left" },
    {
      text: NumberFormatHelper.customerAmountFormat((sale.customer?.balance || 0) + sale.customer_credit_used),
      width: "30%",
      align: "right",
    },
  ]);

  const customerBalanceLabel = isArabic ? "CUSTOMER BALANCE / رصيد العميل:" : "CUSTOMER BALANCE:";

  await addLine([
    { text: customerBalanceLabel, width: "70%", align: "left" },
    {
      text: NumberFormatHelper.customerAmountFormat(sale.customer.balance),
      width: "30%",
      align: "right",
    },
  ]);
}

async function appendInvoiceFooter(addLine, format) {
  if (format?.footer_1) {
    await addLine(format.footer_1, { align: "center" });
  }

  if (format?.footer_2) {
    await addLine(format.footer_2, { align: "center" });
  }

  if (format?.note) {
    for (const note of parseInvoiceNotes(format.note)) {
      await addLine(note, { width: "100%", align: "left" });
    }
  }

  await addLine(`v${APP_VERSION}`, { align: "center" });
}

async function appendMultiLangInvoiceTotals(
  addLine,
  sale,
  shop,
  taxPreference,
  itemsRes,
  isArabic,
  paymentModeOnInvoice,
  format,
  printTotalSave,
) {
  const summaryValueWidth = 14;
  await appendMultiLangInvoiceChargesAndDiscounts(addLine, sale, isArabic);
  await appendMultiLangTaxBreakdown(addLine, sale, shop, taxPreference, isArabic, summaryValueWidth);
  await appendInvoiceTotalSavings(addLine, sale, itemsRes, isArabic, printTotalSave);
  await addLine("=".repeat(lineWidth));

  if (sale?.roundOff !== 0) {
    await addLine([
      { text: "ROUND OFF", width: "50%", align: "left" },
      {
        text: NumberFormatHelper.roundOff(sale.roundOff),
        width: "50%",
        align: "right",
      },
    ]);
  }
  const subTotalLabel = isArabic ? "SUB TOTAL /\n المجموع الفرعي" : "SUB TOTAL";
  await addLine(
    [
      { text: subTotalLabel, width: "50%", align: "left" },
      {
        text: `${shop?.currency_symbol} ${NumberFormatHelper.roundOff(sale?.payable)}`,
        width: "50%",
        align: "right",
      },
    ],
    { size: "double", weight: "bold" },
  );

  await addLine(`In words: ${NumberFormatHelper.toWords(sale?.payable ?? 0)} only`);
  await appendInvoicePaymentModes(addLine, sale, paymentModeOnInvoice);
  await appendInvoiceCustomerBalance(addLine, sale, isArabic);
  await appendInvoiceFooter(addLine, format);
}

function createBitmapAddLine(lines) {
  return async (text, opts = {}) => {
    if (!text) return;
    const { size = "normal", weight = "normal", align = "left", lineHeight } = opts;
    const fontSize = size === "double" ? 32 : 24;
    const fontWeight = weight === "bold" ? "bold" : "normal";

    const bitmap = await textToEscPosBytes(text, { fontSize, fontWeight, align, lineHeight });
    if (bitmap) lines.push(bitmap);
  };
}

export async function getMultiLanguageThreeInchInvoice(sale = {}, shop = {}, titles = [], options = {}) {
  const isArabic = !!options.isArabic;
  let lines = [];
  const addLine = createBitmapAddLine(lines);

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  await appendReceiptLogoIfPresent(lines, shop?.shop_logo);

  const terminalSettings = TerminalHelper.getTerminalSettings() || {};
  const {
    printMrpOnInvoice: showMRP,
    printHsnOnInvoice: showHSN,
    paymentModeOnInvoice,
    printTotalSave,
  } = terminalSettings;
  const taxPreference = localStorage.getItem(STORAGE_KEYS.TAX_PREFERENCE);
  const formattedDateTime = DateTimeHelpers.formatDateAndTime(sale.time);
  const format = getInvoiceFormat();

  await appendMultiLangInvoiceMeta(addLine, format, titles, sale, isArabic, formattedDateTime);
  const itemsList = sale.items || [];
  const itemsRes = await appendMultiLangInvoiceItems(
    addLine,
    itemsList,
    showMRP,
    showHSN,
    terminalSettings.invoiceDisplayRate,
    isArabic,
  );
  await appendMultiLangInvoiceTotals(
    addLine,
    sale,
    shop,
    taxPreference,
    itemsRes,
    isArabic,
    showMRP,
    itemsList,
    paymentModeOnInvoice,
    format,
    printTotalSave,
  );
  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);

  const duplicate = (titles || []).some((title) => (title || "") === SALE_INVOICE_TITLES.DUPLICATE);

  if (!duplicate && hasCashTender(sale)) {
    lines.push(PRINTER_COMMANDS.OPEN_DRAWER);
  }

  return mergeBytes(lines);
}

async function appendKotHeader(lines, sale, titles = [], formattedDate, formattedTime) {
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes(`KOT NO: ${sale?.local_id}\n\n`));

  for (const title of titles) {
    lines.push(textToBytes(`${title}\n\n`));
  }

  if (sale?.order_type) {
    lines.push(textToBytes(`${sale.order_type.toUpperCase()}\n\n`));
  }

  if (sale?.kitchen_name) {
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_BOLD);
    lines.push(textToBytes(`${sale.kitchen_name.toUpperCase()}\n\n`));
  }

  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
  lines.push(textToBytes(padToTwoSides(`Date: ${formattedDate}`, `Time: ${formattedTime}`)));

  const customer = sale?.customer;
  if (sale?.online_order_id != null) {
    const onlineOrder = await OnlineOrderHelper.getOnlineOrder(sale?.online_order_id);
    const customerDetails = onlineOrder?.details?.customer || {};

    Object.entries(customerDetails).forEach(([key, value]) => {
      const formattedKey = key.replace(/_/g, " ").toUpperCase();
      const formattedValue = String(value).replace(/_/g, " ").toUpperCase();
      lines.push(textToBytes(padToTwoSides(formattedKey, formattedValue)));
    });
  } else if (customer) {
    if (customer.name || customer.mobile) {
      lines.push(textToBytes(padToTwoSides(sale.customer.name || "", sale.customer.mobile || "")));
    }
  }

  if (sale?.waiter?.name) {
    lines.push(textToBytes(`Staff: ${sale.waiter.name}\n`));
  }
}

async function appendKotItemList(lines, items = [], showItems = true, strock = false) {
  // @skip-max-lines
  if (!showItems) return;
  if (items.length > 0) {
    lines.push(textToBytes("=".repeat(lineWidth)));
    lines.push(textToBytes(`#`.padEnd(3) + `Item`.padEnd(37) + `Qty`.padStart(8)));
    lines.push(textToBytes("-".repeat(lineWidth)));

    let totalQty = 0;
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      const note = item.latestNote || item.note || "";
      const name = `${item.name || "-"}${item?.option?.label ? ` - ${item.option.label}` : ""} ${note ? `[${note}]` : ""}`;
      const displayQty = item.qty ?? 0;
      totalQty += Number(displayQty);

      const serialText = String(index + 1).padEnd(2) + " ";
      const indexWidth = 3;
      const numericWidth = 8;
      const availableNameWidth = lineWidth - numericWidth - indexWidth;

      const formattedQty = NumberFormatHelper.quantityRoundOffString(displayQty ?? 0);
      const qtyPart = String(formattedQty).padStart(numericWidth);

      if (name.length <= availableNameWidth) {
        if (strock) {
          lines.push(PRINTER_COMMANDS.INVERT_TEXT_ON);
        }
        lines.push(textToBytes(`${serialText}${name.padEnd(availableNameWidth)}${qtyPart}\n`));
        if (strock) {
          lines.push(PRINTER_COMMANDS.INVERT_TEXT_OFF);
        }
      } else {
        const nameWidth = lineWidth - indexWidth;
        const nameLines = wordWrap(name, nameWidth);
        nameLines.forEach((line, i) => {
          const prefix = i === 0 ? serialText : " ".repeat(indexWidth);
          if (strock) {
            lines.push(PRINTER_COMMANDS.INVERT_TEXT_ON);
          }
          lines.push(textToBytes(`${prefix}${line}\n`));
          if (strock) {
            lines.push(PRINTER_COMMANDS.INVERT_TEXT_OFF);
          }
        });
        lines.push(textToBytes(" ".repeat(lineWidth - numericWidth) + qtyPart + "\n"));
      }
    }

    lines.push(textToBytes("-".repeat(lineWidth)));
    lines.push(textToBytes(NumberFormatHelper.quantityRoundOffString(totalQty).padStart(lineWidth)));
  } else {
    lines.push(textToBytes("=".repeat(lineWidth)));
    lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
    lines.push(textToBytes("No items\n"));
    lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
    lines.push(textToBytes("-".repeat(lineWidth)));
  }
}

async function appendKotFooter(lines, sale, notes = []) {
  if (notes.length > 0) {
    lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
    lines.push(textToBytes(`\nNOTES:\n`));
    for (const note of notes) {
      lines.push(textToBytes(`${note}\n`));
    }
    lines.push(textToBytes("-".repeat(lineWidth)));
  }
  if (sale?.table) {
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
    const chairSuffix = sale?.table?.chair?.name ? ` > ${sale.table.chair.name}` : "";
    const tableName = `${sale?.table?.area?.name || "-"} > ${sale?.table?.name || "-"}${chairSuffix}`;
    lines.push(textToBytes(`\n${tableName}`));
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  }

  if (sale?.token_no) {
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
    lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
    lines.push(textToBytes(`\nTOKEN NO: ${sale.token_no}\n`));
  }
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
}

export async function getThreeInchKOT(sale = {}, titles = [], notes = [], strock = false) {
  const lines = [];
  const formattedDate = DateTimeHelpers.getDate(sale?.kotTime);
  const formattedTime = DateTimeHelpers.getLocalTime(sale?.kotTime);
  const items = sale.items || [];
  const showItems = sale?.showItems !== false;

  await appendKotHeader(lines, sale, titles, formattedDate, formattedTime);
  await appendKotItemList(lines, items, showItems, strock);
  await appendKotFooter(lines, sale, notes);

  return mergeBytes(lines);
}

async function appendMultiLangKotHeader(addLine, sale, titles = [], formattedDate, formattedTime) {
  await addLine(`KOT NO: ${sale?.local_id}`, { size: "double", weight: "bold", align: "center" });

  for (const title of titles) {
    await addLine(`[${title}]`, { size: "double", weight: "bold", align: "center" });
    await addLine(textToBytes("\n"));
  }

  if (sale?.order_type) {
    await addLine(sale?.order_type.toUpperCase(), { size: "double", weight: "bold", align: "center" });
    await addLine(textToBytes("\n"));
  }

  if (sale?.kitchen_name) {
    await addLine(sale.kitchen_name.toUpperCase(), { size: "double", weight: "bold", align: "center" });
    await addLine(textToBytes("\n"));
  }

  await addLine([
    { text: `Date: ${formattedDate}`, width: "50%", align: "left" },
    { text: `Time: ${formattedTime}`, width: "50%", align: "right" },
  ]);

  const customer = sale?.customer;
  if (sale?.online_order_id != null) {
    const onlineOrder = await OnlineOrderHelper.getOnlineOrder(sale?.online_order_id);
    const customerDetails = onlineOrder?.details?.customer || {};

    Object.entries(customerDetails).forEach(([key, value]) => {
      const formattedKey = key.replace(/_/g, " ").toUpperCase();
      const formattedValue = String(value).replace(/_/g, " ").toUpperCase();

      addLine(padToTwoSides(formattedKey, formattedValue));
    });
  } else if (customer) {
    if (customer.name || customer.mobile) {
      await addLine(padToTwoSides(sale.customer.name || "", sale.customer.mobile || ""));
    }
  }

  if (sale?.waiter?.name) {
    await addLine(`Staff: ${sale.waiter.name}`);
  }
}

async function appendMultiLangKotItemList(addLine, items = [], strock = false) {
  if (items.length > 0) {
    await addLine("=".repeat(lineWidth));
    await addLine([
      { text: "#", width: "8%", align: "left" },
      { text: "Item", width: "77%", align: "left" },
      { text: "Qty", width: "15%", align: "right" },
    ]);
    await addLine("-".repeat(lineWidth));

    let totalQty = 0;
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      const name = `${item.name || "-"}${item?.option?.label ? ` - ${item.option.label}` : ""} ${item.note ? `[${item.note}]` : ""}`;
      const displayQty = item.qty ?? 0;
      totalQty += Number(displayQty);

      await addLine(
        [
          { text: String(index + 1), width: "8%", align: "left" },
          { text: name, width: "77%", align: "left" },
          { text: String(NumberFormatHelper.quantityRoundOffString(displayQty ?? 0)), width: "15%", align: "right" },
        ],
        { invert: !!strock },
      );
    }

    await addLine("-".repeat(lineWidth));
    await addLine([{ text: NumberFormatHelper.quantityRoundOffString(totalQty), width: "100%", align: "right" }]);
  }
}

export async function getMultiLanguageThreeInchKOT(sale = {}, titles = [], options = {}, strock = false) {
  const lines = [];
  const formattedDate = DateTimeHelpers.getDate(sale?.kotTime);
  const formattedTime = DateTimeHelpers.getLocalTime(sale?.kotTime);

  const addLine = async (text, opts = {}) => {
    if (!text) return;
    const { size = "normal", weight = "normal", align = "left", invert = false } = opts;
    const fontSize = size === "double" ? 32 : 24;
    const fontWeight = weight === "bold" ? "bold" : "normal";
    const bitmap = await textToEscPosBytes(text, { fontSize, fontWeight, align, invert });
    if (bitmap) lines.push(bitmap);
  };

  const items = sale.items || [];
  const showItems = sale?.showItems !== false;

  await appendMultiLangKotHeader(addLine, sale, titles, formattedDate, formattedTime);
  if (showItems) {
    await appendMultiLangKotItemList(addLine, items, strock);
  }

  if (options?.notes?.length > 0) {
    for (const note of options?.notes) {
      await addLine(note);
    }
  }

  if (sale?.table) {
    const chairSuffix = sale?.table?.chair?.name ? ` > ${sale.table.chair.name}` : "";
    const tableName = `${sale?.table?.area?.name || "-"} > ${sale?.table?.name || "-"}${chairSuffix}`;
    await addLine(`\n${tableName}`, { size: "double", weight: "bold" });
  }

  if (sale?.token_no) {
    await addLine(`\nTOKEN NO: ${sale.token_no}`, { size: "double", weight: "bold", align: "center" });
  }

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);
  return mergeBytes(lines);
}

function appendPaymentReceiptContent(lines, payment, formattedDate, formattedTime) {
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_BOLD);
  lines.push(textToBytes(`PAYMENT RECEIPT\n`));
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);

  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
  lines.push(textToBytes(padToTwoSides(`Date: ${formattedDate}`, `Time: ${formattedTime}`)));

  if (payment?.customer) {
    lines.push(textToBytes(padToTwoSides(payment.customer.name || "", payment.customer.mobile || "")));
  }
  lines.push(textToBytes("=".repeat(lineWidth)));
  lines.push(textToBytes("Payment Description\n"));
  lines.push(textToBytes("-".repeat(lineWidth)));

  const note = payment.note || "Payment received";
  lines.push(textToBytes(`${note}\n`));
  lines.push(textToBytes(`Payment at ${formattedDate} ${formattedTime}\n`));
  lines.push(textToBytes("-".repeat(lineWidth)));

  if (payment?.tenders && payment.tenders.length > 0) {
    payment.tenders.forEach((t) => {
      lines.push(textToBytes(padToTwoSides(`${t.payment_mode?.name || "-"}`, NumberFormatHelper.roundOff(t.amount))));
    });
  } else if (payment?.amount) {
    lines.push(
      textToBytes(padToTwoSides(`${payment.payment_mode || "PAID"}`, NumberFormatHelper.roundOff(payment.amount))),
    );
  }

  lines.push(textToBytes(padToTwoSides(`PAID:`, NumberFormatHelper.roundOff(payment.amount || payment.credit || 0))));

  if (payment?.customer?.balance !== undefined) {
    lines.push(
      textToBytes(padToTwoSides(`CUSTOMER BALANCE`, NumberFormatHelper.customerAmountFormat(payment.customer.balance))),
    );

    const inWords = NumberFormatHelper.toWords(payment.customer.balance || 0);

    if (inWords) {
      lines.push(textToBytes(`In words: ${inWords} only\n`));
    }
  }
}

export async function getThreeInchCustomerPaymentReceipt(payment = {}, shop = {}) {
  const lines = [];
  const dateObj = payment?.time ? new Date(payment.time) : new Date();
  const formattedDate = DateTimeHelpers.getDate(dateObj);
  const formattedTime = DateTimeHelpers.getLocalTime(dateObj);

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  if (shop?.shop_logo) {
    const logoBytes = await getLogoEscPosBytes(shop.shop_logo);
    if (logoBytes != null) {
      lines.push(logoBytes);
      lines.push(textToBytes("\n"));
    }
  }

  await buildInvoiceHeader(lines, { skipCustomHeader: true, sale: payment });
  appendPaymentReceiptContent(lines, payment, formattedDate, formattedTime);

  lines.push(textToBytes("\n"));
  const invoiceFormat = getInvoiceFormat();
  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  if (invoiceFormat.footer_1) lines.push(textToBytes(`${invoiceFormat.footer_1}\n`));
  if (invoiceFormat.footer_2) lines.push(textToBytes(`${invoiceFormat.footer_2}\n`));

  lines.push(textToBytes(`v${APP_VERSION}\n`));
  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);

  return mergeBytes(lines);
}

async function appendPaymentReceiptHeader(addLine, shop, invoiceFormat) {
  if (invoiceFormat?.shop_name)
    await addLine(invoiceFormat.shop_name, { size: "double", weight: "bold", align: "center" });
  if (invoiceFormat?.address_1) await addLine(invoiceFormat.address_1, { align: "center" });
  if (invoiceFormat?.address_2) await addLine(invoiceFormat.address_2, { align: "center" });
  if (invoiceFormat?.address_3) await addLine(invoiceFormat.address_3, { align: "center" });
  if (invoiceFormat?.address_4) await addLine(invoiceFormat.address_4, { align: "center" });
  if (invoiceFormat?.tel_no) await addLine(`Phone: +${invoiceFormat.tel_no}`, { align: "center" });
  if (invoiceFormat?.tin) await addLine(invoiceFormat.tin, { align: "center" });
}

async function appendCustomerPaymentReceiptTitle(addLine, formattedDate, formattedTime, isArabic) {
  await addLine("PAYMENT RECEIPT", {
    size: "double",
    weight: "bold",
    align: "center",
  });

  const dateLabel = isArabic ? "Date / تاريخ" : "Date";
  const timeLabel = isArabic ? "Time / الوقت" : "Time";

  await addLine(padToTwoSides(`${dateLabel}: ${formattedDate}`, `${timeLabel}: ${formattedTime}`));
}

async function appendCustomerPaymentDescription(addLine, payment, formattedDate, formattedTime, isArabic) {
  const descLabel = isArabic ? "Payment Description / وصف الدفع" : "Payment Description";

  await addLine("=".repeat(lineWidth));
  await addLine(descLabel, { weight: "bold" });
  await addLine("-".repeat(lineWidth));

  await addLine(payment.note || "Payment received");
  await addLine(`Payment at ${formattedDate} ${formattedTime}`);

  await addLine("-".repeat(lineWidth));
}

async function appendCustomerPaymentTenders(addLine, payment) {
  if (!payment?.tenders?.length) return;

  for (const tender of payment.tenders) {
    await addLine([
      {
        text: tender.payment_mode?.name || "-",
        width: "70%",
        align: "left",
      },
      {
        text: NumberFormatHelper.roundOff(tender.amount),
        width: "30%",
        align: "right",
      },
    ]);
  }
}

async function appendCustomerPaymentSummary(addLine, payment, isArabic) {
  const paidLabel = isArabic ? "PAID / مدفوع:" : "PAID:";

  await addLine([
    {
      text: paidLabel,
      width: "70%",
      align: "left",
      size: 32,
      weight: "bold",
    },
    {
      text: NumberFormatHelper.roundOff(payment.amount || 0),
      width: "30%",
      align: "right",
      size: 32,
      weight: "bold",
    },
  ]);

  if (payment?.customer?.balance === undefined) return;

  const balanceLabel = isArabic ? "CUSTOMER BALANCE / رصيد العميل" : "CUSTOMER BALANCE";

  const inWordsLabel = isArabic ? "In words / بالكلمات" : "In words";

  await addLine([
    { text: balanceLabel, width: "70%", align: "left" },
    {
      text: NumberFormatHelper.customerAmountFormat(payment.customer.balance),
      width: "30%",
      align: "right",
    },
  ]);

  await addLine(`${inWordsLabel}: ${NumberFormatHelper.toWords(payment.customer.balance || 0)} only`);
}

async function appendCustomerPaymentFooter(addLine, invoiceFormat) {
  if (invoiceFormat.footer_1) {
    await addLine(invoiceFormat.footer_1, {
      align: "center",
    });
  }

  await addLine(`v${APP_VERSION}`, {
    align: "center",
  });
}

export async function getMultiLanguageThreeInchCustomerPaymentReceipt(payment = {}, shop = {}, options = {}) {
  const isArabic = !!options.isArabic;
  const lines = [];
  const dateObj = payment?.time ? new Date(payment.time) : new Date();
  const formattedDate = DateTimeHelpers.getDate(dateObj);
  const formattedTime = DateTimeHelpers.getLocalTime(dateObj);

  const addLine = createBitmapAddLine(lines);

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);

  if (shop?.shop_logo) {
    const logoBytes = await getLogoEscPosBytes(shop.shop_logo);
    if (logoBytes) {
      lines.push(logoBytes);
      lines.push(textToBytes("\n"));
    }
  }

  const invoiceFormat = getInvoiceFormat();

  await appendPaymentReceiptHeader(addLine, shop, invoiceFormat);

  await appendCustomerPaymentReceiptTitle(addLine, formattedDate, formattedTime, isArabic);

  if (payment?.customer) {
    await addLine(padToTwoSides(payment.customer.name || "", payment.customer.mobile || ""));
  }

  await appendCustomerPaymentDescription(addLine, payment, formattedDate, formattedTime, isArabic);

  await appendCustomerPaymentTenders(addLine, payment);

  await appendCustomerPaymentSummary(addLine, payment, isArabic);

  await appendCustomerPaymentFooter(addLine, invoiceFormat);

  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);

  return mergeBytes(lines);
}

function renderSingleLargeKotItem(lines, item) {
  const itemName = item.name || "-";
  const formattedQty = NumberFormatHelper.quantityRoundOffString(item.qty ?? 0);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);

  const qtyWidth = 3;
  const doubleLineWidth = 24;
  const availableNameWidth = doubleLineWidth - qtyWidth;

  const hasLargeOption = item?.option?.label && !item.option.label.toUpperCase().startsWith("IN:");
  const nameToPrint = hasLargeOption ? `${itemName} ${item.option.label}` : itemName;

  const nameLines = wordWrap(nameToPrint, availableNameWidth);
  nameLines.forEach((line, i) => {
    const prefix = i === 0 ? formattedQty.toString().padEnd(qtyWidth) : " ".repeat(qtyWidth);
    lines.push(textToBytes(`${prefix}${line}\n`));
  });

  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);

  if (item.option?.label && item.option.label.toUpperCase().startsWith("IN:")) {
    const optionLines = wordWrap(item.option.label, 41);
    optionLines.forEach((line) => {
      lines.push(textToBytes(`       ${line}\n`));
    });
  }

  const note = item.latestNote || item.note || "";
  if (note) {
    const noteText = `NOTES: ${note}`;
    const noteLines = wordWrap(noteText, 41);
    noteLines.forEach((line) => {
      lines.push(textToBytes(`       ${line}\n`));
    });
  }
}

async function appendLargeKotItemList(lines, items = [], strock = false) {
  if (items.length > 0) {
    lines.push(textToBytes("=".repeat(lineWidth)));
    lines.push(textToBytes("QTY".padEnd(7) + "ITEM NAME\n"));
    lines.push(textToBytes("-".repeat(lineWidth)));
    if (strock) {
      lines.push(PRINTER_COMMANDS.INVERT_TEXT_ON);
    }
    for (let index = 0; index < items.length; index++) {
      renderSingleLargeKotItem(lines, items[index], strock);
    }
    if (strock) {
      lines.push(PRINTER_COMMANDS.INVERT_TEXT_OFF);
    }
    lines.push(textToBytes("-".repeat(lineWidth)));
  }
}

export async function getThreeInchLargeKOT(sale = {}, titles = [], notes = [], strock = false) {
  const lines = [];
  const formattedDate = DateTimeHelpers.getDate(sale?.kotTime);
  const formattedTime = DateTimeHelpers.getLocalTime(sale?.kotTime);
  const items = sale.items || [];
  const showItems = sale?.showItems !== false;

  await appendKotHeader(lines, sale, titles, formattedDate, formattedTime);
  if (showItems) {
    await appendLargeKotItemList(lines, items, strock);
  }
  await appendKotFooter(lines, sale, notes);

  return mergeBytes(lines);
}

export function getTestKotReceipt({ categoryName = "", kitchenName = "", kitchenIp = "" }) {
  const lines = [];

  lines.push(PRINTER_COMMANDS.ALIGN_CENTER);
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_DOUBLE);
  lines.push(textToBytes("KOT TEST\n\n"));
  if (kitchenName) {
    lines.push(PRINTER_COMMANDS.CHARS_SIZE_BOLD);
    lines.push(textToBytes(`${kitchenName.trim().toUpperCase()}\n\n`));
  }
  lines.push(PRINTER_COMMANDS.CHARS_SIZE_NORMAL);
  lines.push(PRINTER_COMMANDS.ALIGN_LEFT);
  const labelWidth = 9;
  lines.push(textToBytes("-".repeat(lineWidth) + "\n"));
  lines.push(textToBytes(`${"Category".padEnd(labelWidth)}: ${categoryName || "-"}\n`));
  if (kitchenName) {
    lines.push(textToBytes(`${"Kitchen".padEnd(labelWidth)}: ${kitchenName.trim()}\n`));
  }
  lines.push(textToBytes(`${"IP".padEnd(labelWidth)}: ${kitchenIp}\n`));
  lines.push(textToBytes(`${"Time".padEnd(labelWidth)}: ${DateTimeHelpers.formatDateTime(new Date())}\n`));
  lines.push(textToBytes("-".repeat(lineWidth) + "\n"));
  lines.push(PRINTER_COMMANDS.FEED_PAPER);
  lines.push(PRINTER_COMMANDS.CUT_CHARS);
  lines.push(PRINTER_COMMANDS.BEEP_SOUND);

  return mergeBytes(lines);
}

export async function openCashDrawer() {
  const bridge = AppBridgeHelper.getBridge();
  if (!bridge) {
    toast.error("Printer bridge not available");
    return false;
  }

  try {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const targetType = localStorage.getItem(STORAGE_KEYS.INVOICE_TARGET_TYPE);
    const targetValue = localStorage.getItem(STORAGE_KEYS.INVOICE_TARGET_VALUE);

    let { printerType, printerTarget } = TerminalHelper.getDefaultPrinterConfig(terminalSettings);

    if (targetType === INVOICE_TARGET_TYPE.IP && targetValue) {
      printerType = PRINTER_TYPES.LAN;
      printerTarget = targetValue;
    } else if (printerType === PRINTER_TYPES.USB || printerType === PRINTER_TYPES.DIRECT_USB) {
      if (!printerTarget || printerTarget.toLowerCase() === "default") {
        printerTarget = null;
      }
    }

    if (printerType === PRINTER_TYPES.LAN && !printerTarget) {
      toast.error("Printer IP is not configured");
      return false;
    }

    const content = bytesToBase64(PRINTER_COMMANDS.OPEN_DRAWER);
    await PrinterManager.print({
      content,
      printerType,
      printerTarget,
      label: "OPEN CASH DRAWER",
    });
    return true;
  } catch (err) {
    console.error("Failed to open cash drawer:", err);
    toast.error("Failed to open cash box");
    return false;
  }
}
